import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react'

export interface ConfirmDialogProps {
  /**
   * Заголовок называет предмет действия: «Удалить сценарий „Приветствие“?»,
   * а не «Вы уверены?». Второе спрашивает о чувствах, первое — о факте, и
   * только по нему видно, что человек попал не в ту строку.
   */
  title: string
  /** Что исчезнет вместе с предметом действия. */
  children: ReactNode
  /** Подпись подтверждающей кнопки — тем же глаголом, что и на вызвавшей. */
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
  /** Действие уже отправлено — кнопка подтверждения блокируется. */
  busy?: boolean
}

/** Что вообще может получить фокус внутри окна. */
const ФОКУСИРУЕМЫЕ = 'button:not(:disabled), [href], input:not(:disabled), select, textarea, [tabindex]:not([tabindex="-1"])'

/**
 * Окно подтверждения необратимого действия.
 *
 * Отрисовывается только пока нужно: показанное всегда, но скрытое стилями,
 * оно оставалось бы в порядке обхода клавиатурой и в дереве доступности.
 *
 * Про фокус здесь три отдельных обещания, и каждое существует по своей
 * причине. Он ставится на отмену, а не на подтверждение: клавиша пробела,
 * нажатая по инерции сразу после «Удалить», не должна удалять. Он не
 * выходит за пределы окна: за окном лежит экран, на котором человек сейчас
 * ничего сделать не может, а табуляция туда уводит и создаёт ощущение, что
 * окно не модальное. И он возвращается на вызвавшую кнопку: без этого
 * закрытие окна отправляет фокус в начало страницы, и работавший с
 * клавиатуры теряет место в списке.
 */
export function ConfirmDialog(props: ConfirmDialogProps) {
  const { title, children, confirmLabel, onConfirm, onCancel, busy = false } = props
  const titleId = useId()
  const окно = useRef<HTMLDivElement>(null)
  const отмена = useRef<HTMLButtonElement>(null)
  // Подтверждение выполняется ровно один раз: между нажатием и закрытием
  // окна проходит перерисовка, и за это время двойной щелчок успевает
  // отправить второе удаление того, чего уже нет.
  const выполнено = useRef(false)

  useEffect(() => {
    const вернуться = document.activeElement
    отмена.current?.focus()
    return () => {
      if (вернуться instanceof HTMLElement) вернуться.focus()
    }
  }, [])

  const подтвердить = useCallback(() => {
    if (выполнено.current) return
    выполнено.current = true
    onConfirm()
  }, [onConfirm])

  function приНажатииКлавиши(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation()
      onCancel()
      return
    }
    if (event.key !== 'Tab') return

    const элементы = [...(окно.current?.querySelectorAll<HTMLElement>(ФОКУСИРУЕМЫЕ) ?? [])]
    if (элементы.length === 0) return
    const первый = элементы[0] as HTMLElement
    const последний = элементы[элементы.length - 1] as HTMLElement

    // Замыкаем круг руками: браузер сам этого не делает, а `inert` на
    // остальной странице потребовал бы от окна знать, что вокруг него.
    if (event.shiftKey && document.activeElement === первый) {
      event.preventDefault()
      последний.focus()
    } else if (!event.shiftKey && document.activeElement === последний) {
      event.preventDefault()
      первый.focus()
    }
  }

  return (
    <div
      className="модалка"
      onMouseDown={(event) => {
        // Щелчок мимо окна — отмена: безопасный исход, и он же ожидаемый.
        if (event.target === event.currentTarget) onCancel()
      }}
    >
      <div
        className="модалка__окно"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={окно}
        onKeyDown={приНажатииКлавиши}
      >
        <h2 className="модалка__заголовок" id={titleId}>
          {title}
        </h2>
        <div className="модалка__тело">{children}</div>
        <div className="модалка__действия">
          {/* Отмена идёт первой и по порядку обхода, и по счёту слева:
              безопасный выход не прячется за разрушительным. */}
          <button type="button" className="btn" ref={отмена} onClick={onCancel}>
            Отмена
          </button>
          <button type="button" className="btn btn--danger" disabled={busy} onClick={подтвердить}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export interface Confirmation<T> {
  /** Что подтверждают прямо сейчас; `null` — окна нет. */
  pending: T | null
  ask: (target: T) => void
  cancel: () => void
}

/**
 * Состояние «спрашиваем про вот это». Отдельный хук, потому что экраны
 * повторяют его слово в слово: список сценариев, номеров, ключей и
 * переменных спрашивают об одном и том же, отличаясь только тем, о чём
 * именно.
 */
export function useConfirmation<T>(): Confirmation<T> {
  const [pending, setPending] = useState<T | null>(null)
  return {
    pending,
    // Через функцию-обновитель: цель может сама оказаться функцией, и
    // React принял бы её за обновитель состояния.
    ask: (target: T) => setPending(() => target),
    cancel: () => setPending(null),
  }
}
